import React, { Component } from 'react';
import { RichUtils, EditorState, Modifier } from 'draft-js';
import { withStyles } from '@material-ui/core/styles';
import cx from 'classnames';
import PropTypes from 'prop-types';
import IconButton from '@material-ui/core/IconButton';

const styles = theme => ({
  root: {
    color: 'white',
  },
});

const getValidBlockBefore = (content, endKey) => {
  let blockBefore = null;
  let error = false;
  let lastKey = endKey;
  while (!blockBefore) {
    const potentialBlock = content.getBlockBefore(lastKey);
    if (!potentialBlock) {
      error = true;
      return;
    }
    if (potentialBlock.getLength() > 1) {
      blockBefore = potentialBlock;
    } else {
      lastKey = potentialBlock.getKey();
    }
  }
  if (error) throw `Could not find a valid Block before endkey '${endKey}'`;
  return blockBefore;
};

const toggleBlockType = (editorState, blockType) => {
  const selection = editorState.getSelection();
  const startKey = selection.getStartKey();
  let endKey = selection.getEndKey();
  const content = editorState.getCurrentContent();
  let target = selection;

  // Triple-click can lead to a selection that includes offset 0 of the
  // following block. The `SelectionState` for this case is accurate, but
  // we should avoid toggling block type for the trailing block because it
  // is a confusing interaction.
  if (startKey !== endKey && selection.getEndOffset() === 0) {
    const blockBefore = getValidBlockBefore(content, endKey);
    endKey = blockBefore.getKey();
    target = target.merge({
      anchorKey: startKey,
      anchorOffset: selection.getStartOffset(),
      focusKey: endKey,
      focusOffset: blockBefore.getLength(),
      isBackward: false,
    });
  }

  const hasAtomicBlock = content
    .getBlockMap()
    .skipWhile((_, k) => k !== startKey)
    .reverse()
    .skipWhile((_, k) => k !== endKey)
    .some(v => v.getType() === 'atomic');

  if (hasAtomicBlock) {
    return editorState;
  }

  const typeToSet =
    content.getBlockForKey(startKey).getType() === blockType
      ? 'unstyled'
      : blockType;

  return EditorState.push(
    editorState,
    Modifier.setBlockType(content, target, typeToSet),
    'change-block-type',
  );
};

const createBlockTypeButton = ({ blockType, children }) =>
  @withStyles(styles)
  class BlockTypeButton extends Component {
    static serviceName = 'blockTypeButton';
    static blockType = blockType;
    static displayName = `BlockTypeButton-${blockType}`;

    static propTypes = {
      setEditorState: PropTypes.func.isRequired,
      editorState: PropTypes.object.isRequired,
      isHidden: PropTypes.bool.isRequired,
    };

    get isActive() {
      const { editorState } = this.props;
      if (!editorState) return false;

      const type = editorState
        .getCurrentContent()
        .getBlockForKey(editorState.getSelection().getStartKey())
        .getType();
      return type === blockType;
    }

    preventDefault(e) {
      e.preventDefault();
    }

    toggleStyle = e => {
      const selection = this.props.editorState.getSelection();
      e.preventDefault();
      // We could use draft js richutils here but this has a problem with certain selections (e.g. triple clicks)
      // see: https://github.com/facebook/draft-js/issues/86
      // there is a fix up but somehow it doesn't when the selection is larger than "one block ahead". This happens
      // currently in any "atomic" block that has a caption. if you e.g. tripple click the text block before a media block
      // the selection does not just include the media but also the caption block, therefore the fix in draft js rich
      // utils doesn't work
      const newEditorState = toggleBlockType(this.props.editorState, blockType);
      this.props.setEditorState(newEditorState);
    };

    render() {
      const { isHidden, classes } = this.props;
      if (isHidden) return null;
      return (
        <div className={classes.root} onMouseDown={this.preventDefault}>
          <IconButton
            color={this.isActive ? 'primary' : 'inherit'}
            onClick={this.toggleStyle}
          >
            {children}
          </IconButton>
        </div>
      );
    }
  };

export default createBlockTypeButton;
